<script setup lang="ts">
import type { ButtonVariants } from './components/ui/button';
import type { NuxtError } from '#app';

import ErrorpageData from '~~/data/pages/error';

import { useEvents } from './composables/events/useEvents';
import { MixpanelEvents, MixpanelProps } from './core/service/mixpanel';

const strings = ErrorpageData.pt;

// Add noindex meta tag for error pages to prevent search engines from indexing them
useHead({
	meta: [
		{
			name: 'robots',
			content: 'noindex, nofollow'
		}
	]
});

const { $mixpanel } = useNuxtApp();
const route = useRoute();

interface CtaAnalytics {
	eventName: string;
	props?: Record<string, string>;
}

interface ActionErrorPage {
	variant: ButtonVariants['variant'];
	text: string;
	redirectTo: string;
	showIcon?: boolean;
	analytics?: CtaAnalytics;
}

interface ErrorMessage {
	code?: number;
	title: string;
	message: string;
	actions: ActionErrorPage[];
}

const props = defineProps({
	error: Object as () => NuxtError
});

const { trackPageView, trackPageError } = useEvents();

const errorMessagesMap: Record<number, ErrorMessage> = {
	400: {
		code: 400,
		title: strings.errors[404].title, // Reusing 404 messages for 400 errors
		message: strings.errors[404].message,
		actions: [
			{
				variant: 'secondary-dark',
				text: strings.buttons.goToHome,
				redirectTo: '/',
				showIcon: false,
				analytics: {
					eventName: MixpanelEvents.CTA_HOME_CLICKED
				}
			},
			{
				variant: 'secondary',
				text: strings.buttons.goToProperties,
				redirectTo: '/venda/imoveis/sao-paulo-sp-brasil',
				showIcon: true,
				analytics: {
					eventName: 'cta_view_more_clicked',
					props: {
						section_name: 'error'
					}
				}
			}
		]
	},
	404: {
		code: 404,
		title: strings.errors[404].title,
		message: strings.errors[404].message,
		actions: [
			{
				variant: 'secondary-dark',
				text: strings.buttons.goToHome,
				redirectTo: '/',
				showIcon: false,
				analytics: {
					eventName: MixpanelEvents.CTA_HOME_CLICKED
				}
			},
			{
				variant: 'secondary',
				text: strings.buttons.goToProperties,
				redirectTo: '/venda/imoveis/sao-paulo-sp-brasil',
				showIcon: true,
				analytics: {
					eventName: 'cta_view_more_clicked',
					props: {
						section_name: 'error'
					}
				}
			}
		]
	}
};

const defaultErrorMessage: ErrorMessage = {
	code: 500,
	title: strings.errors.default.title,
	message: strings.errors.default.message,
	actions: [
		{
			variant: 'secondary',
			text: strings.buttons.goToHome,
			redirectTo: '/',
			showIcon: true,
			analytics: {
				eventName: MixpanelEvents.CTA_HOME_CLICKED
			}
		}
	]
};

function getErrorMessage(errorCode: number | null): ErrorMessage {
	return errorCode !== null && errorMessagesMap[errorCode]
		? errorMessagesMap[errorCode]
		: defaultErrorMessage;
}

const errorMessage = computed<ErrorMessage>(() => {
	const errorCode = props.error?.statusCode ?? null;
	return getErrorMessage(errorCode);
});

const getContainerButtonsSpacing = computed<string>(() => {
	return errorMessage.value.actions.length > 1 ? 'px-4' : 'px-52';
});

const generateAnalyticsCTA = (
	analyticsData: CtaAnalytics,
	action: ActionErrorPage
) => {
	return {
		eventName: analyticsData.eventName,
		props: {
			cta_text: action.text,
			current_page: route.fullPath,
			destination_url: action.redirectTo,
			...analyticsData.props
		}
	};
};

const handleLinkClick = (action: ActionErrorPage) => {
	if (action.analytics) {
		const analyticsData = generateAnalyticsCTA(action.analytics, action);
		$mixpanel.track(analyticsData.eventName, analyticsData.props);
	}
};

onMounted(() => {
	// eslint-disable-next-line no-console
	console.log(props.error);
	trackPageView({
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.PREVIOUS_PAGE_URL]: document.referrer,
		[MixpanelProps.EVENT_NAME]: 'page_viewed'
	});
	trackPageError({
		[MixpanelProps.ERROR_CODE]: props.error?.statusCode,
		[MixpanelProps.PAGE_URL]: route.fullPath
	});
});
</script>

<template>
	<NuxtLayout name="default-primary">
		<div
			class="flex w-full flex-col items-center justify-center bg-black px-1 py-20 text-white md:px-16 md:py-48 xl:px-32 2xl:px-96"
		>
			<h2
				v-if="error?.statusCode"
				class="text-[80px] font-light text-beige md:text-[120px]"
			>
				{{ errorMessage?.code }}
			</h2>
			<div
				class="flex w-full flex-col items-center justify-center gap-3 text-center"
			>
				<h3
					class="font-matter-sq text-[32px] font-light tracking-[-0.02em] md:pt-6 lg:text-[44px]"
				>
					{{ errorMessage.title }}
				</h3>
				<h4 class="px-4 text-[16px] md:px-0 lg:text-[18px]">
					{{ errorMessage.message }}
				</h4>
				<div
					class="flex w-full flex-col items-center justify-center space-y-4 py-6 md:flex-row md:space-x-8 md:space-y-0 md:py-8"
					:class="getContainerButtonsSpacing"
				>
					<NuxtLink
						v-for="(action, index) in errorMessage.actions"
						:key="action.text + index"
						v-slot="{ href }"
						:to="action.redirectTo"
						class="w-full"
						custom
						@click="handleLinkClick(action)"
					>
						<Button
							as="a"
							:href="href"
							:variant="action.variant"
							:is-client-only="false"
						>
							{{ action.text }}
							<IconMaterialSymbolsNorthEast
								v-if="action.showIcon"
								class="w-5 md:w-6"
							/>
						</Button>
					</NuxtLink>
				</div>
			</div>
		</div>
	</NuxtLayout>
</template>
